// AppShellHost — the mount of the `chat` module (chat-module-plan S6). The
// shell edge for chat under the client runtime (app/runtime.ts):
//  - `useChatModule` builds the runtime once, mounts the module with the real
//    ports, and projects its Model into React (useSyncExternalStore). Every
//    caller dispatches straight into it (`sendChat`); the runtime decides
//    against its own latest Model, so no caller threads a deferred arrow or
//    a latest-state ref any more.
//  - `useChatSignalRoutes` is the root route table for chat's Signals whose
//    owners are not modules yet (toasts, frame, the world re-read, the
//    conversation list, the context re-read, the editor's landed proposals).

import * as Core from "@bilda/core";
import { match } from "@bilda/utils";
import {
    type Dispatch,
    type MutableRefObject,
    type SetStateAction,
    useEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import type { EditorHostApi } from "../../components/EditorHost/EditorHost.js";
import type { RecentThread } from "../../components/WorkspaceLanding/WorkspaceLanding.js";
import {
    type ChatCommand,
    type ChatDeps,
    type ChatState,
    mkChatModule,
} from "../../modules/chat.js";
import type { Signal } from "../../modules/signal.js";
import type { FrameTransition, HostNotice } from "../frame.js";
import type { LandedProposal } from "../ports.js";
import { mkRuntime, type Runtime } from "../runtime.js";

export type ChatMount = {
    readonly chatState: ChatState;
    readonly sendChat: (command: ChatCommand) => void;
    readonly chatRuntime: Runtime;
};

// Built once per shell; the mount mints the client-only system-row ids.
export const useChatModule = (ports: ChatDeps["ports"]): ChatMount => {
    const [mount] = useState(() => {
        const runtime = mkRuntime();
        let nextSystemId = 0;
        return {
            runtime,
            chat: runtime.mount(
                mkChatModule({
                    ports,
                    mkLocalSystemId: () => `local-system-${nextSystemId++}`,
                }),
            ),
        };
    });
    const { runtime, chat } = mount;
    const chatState = useSyncExternalStore(runtime.subscribe, chat.getModel);
    // A refusal (submit while drafting, stop while idle, …) is swallowed
    // user-silently, visible only in dev.
    const sendChat = (command: ChatCommand): void => {
        const r = chat.dispatch(command);
        if (r._tag === "Result.Err" && import.meta.env.DEV)
            console.debug("[chat] refused", r.error.type, command.type);
    };
    return { chatState, sendChat, chatRuntime: runtime };
};

export type ChatSignalOwners = {
    readonly showNotice: (n: HostNotice) => void;
    readonly dispatch: Dispatch<FrameTransition>;
    readonly setRetrySeq: Dispatch<SetStateAction<number>>;
    readonly noteConversationStarted: (started: RecentThread) => void;
    readonly reloadActiveContext: (anchor: Core.EntityRef.Ref) => Promise<void>;
    readonly editor: EditorHostApi;
    readonly pendingProposalsByMaterialRef: MutableRefObject<
        ReadonlyMap<string, LandedProposal>
    >;
};

export const useChatSignalRoutes = (
    runtime: Runtime,
    owners: ChatSignalOwners,
): void => {
    // The ref keeps the latest closures so the one subscription never goes
    // stale.
    const latest = useRef(owners);
    latest.current = owners;
    useEffect(
        () =>
            runtime.onSignal(
                match<Signal, void>({
                    "Signal.NoticeRaised": ({ notice }) =>
                        latest.current.showNotice(notice),
                    "Signal.FrameRequested": ({ transition }) =>
                        latest.current.dispatch(transition),
                    // F-A / DB1: a turn's tool steps commit resources + KB
                    // edges with no push channel to the client, so the world
                    // is re-projected: this bump re-runs EVERY retrySeq-keyed
                    // read (library, details, conversation summaries, and via
                    // the fresh library the explicit + ambient context).
                    "Signal.WorldChanged": () =>
                        latest.current.setRetrySeq((s) => s + 1),
                    // The started conversation joins the list, which makes it
                    // durable for the shell.
                    "Signal.ConversationStarted": ({ summary }) =>
                        latest.current.noteConversationStarted({
                            ...summary,
                            when: "",
                        }),
                    // The anchor key does not change nascent→durable (same id),
                    // so the tab-switch context effect won't re-fire: reload the
                    // Context so the flushed grounding shows in the durable
                    // ContextSideCard.
                    "Signal.PendingGroundingFlushed": ({ conversationId }) =>
                        void latest.current.reloadActiveContext(
                            Core.Conversation.Ref.mk(
                                Core.Conversation.Id.mk(conversationId),
                                Core.Versioning.Version.v1,
                            ),
                        ),
                    // §4b: EditorHost folds a landed proposal into its own
                    // loop/history; when that material's editor isn't mounted
                    // the proposal is held (never lost).
                    // Chat itself handles a stop request (its inbound).
                    "Signal.StopRequested": () => undefined,
                    "Signal.ProposalLanded": ({ target, landed }) => {
                        const { editor, pendingProposalsByMaterialRef: held } =
                            latest.current;
                        if (editor.applyLandedProposal(target, landed)) return;
                        held.current = new Map(held.current).set(
                            target,
                            landed,
                        );
                    },
                }),
            ),
        [runtime],
    );
};
